export const TRACKING_IDS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'fbc', 'fbp'] as const;
export type TrackingIdKey = (typeof TRACKING_IDS)[number];
export type TrackingIds = Partial<Record<TrackingIdKey, string>>;

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;
export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

// AdTrackingProvider's session blob: a second place a UTM may already be sitting
// if it arrived on an earlier page of this session.
const AD_TRACKING_STORAGE_KEY = 'ad_tracking_data';

const GOOGLE_FAMILY: TrackingIdKey[] = ['gclid', 'gbraid', 'wbraid'];
// Click evidence only. `fbc` is the pixel's cookie copy of an fbclid, so it survives the URL
// losing the param; `fbp` is set for every visitor on first page view and proves no click.
// It is still captured (Meta uses it to match server events), it just never decides attribution.
const META_CLICK: TrackingIdKey[] = ['fbclid', 'fbc'];

const WINDOW_DAYS: Record<TrackingIdKey, number> = {
  gclid: 90,
  gbraid: 90,
  wbraid: 90,
  fbclid: 28,
  fbc: 28,
  fbp: 28,
};

export interface AttributionTrail {
  captured_at: string;
  url: TrackingIds;
  session: TrackingIds;
  posthog: {
    last_touch: Record<string, string | null>;
    first_touch: Record<string, string | null>;
  };
  resolved: {
    last_touch: TrackingIds;
    first_touch: TrackingIds;
  };
  utm: {
    url: UtmParams;
    session: UtmParams;
    resolved: UtmParams;
  };
}

export interface AdAttribution {
  lastTouch: TrackingIds;
  firstTouch: TrackingIds;
  utm: UtmParams;
  trail: AttributionTrail;
}

export interface PostHogReader {
  get_property: (key: string) => unknown;
}

interface SessionStorageReader {
  getItem: (key: string) => string | null;
}

function readCookie(cookies: string, name: string): string | undefined {
  const re = new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)');
  const match = cookies.match(re);
  return match ? decodeURIComponent(match[1]) : undefined;
}

function readUrl(params: URLSearchParams, cookies: string): TrackingIds {
  const out: TrackingIds = {};
  for (const id of TRACKING_IDS) {
    if (id === 'fbc' || id === 'fbp') continue;
    const v = params.get(id);
    if (v) out[id] = v;
  }
  const fbc = readCookie(cookies, '_fbc');
  if (fbc) out.fbc = fbc;
  const fbp = readCookie(cookies, '_fbp');
  if (fbp) out.fbp = fbp;
  return out;
}

function readSession(storage: SessionStorageReader | null): TrackingIds {
  if (!storage) return {};
  const out: TrackingIds = {};
  for (const id of TRACKING_IDS) {
    const v = storage.getItem(id);
    if (v) out[id] = v;
  }
  return out;
}

function readUtmFromUrl(params: URLSearchParams): UtmParams {
  const out: UtmParams = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) out[key] = v;
  }
  return out;
}

function readUtmFromSession(storage: SessionStorageReader | null): UtmParams {
  if (!storage) return {};
  const out: UtmParams = {};

  for (const key of UTM_KEYS) {
    const v = storage.getItem(key);
    if (v) out[key] = v;
  }

  try {
    const blob = storage.getItem(AD_TRACKING_STORAGE_KEY);
    if (blob) {
      const parsed = JSON.parse(blob) as Record<string, unknown>;
      for (const key of UTM_KEYS) {
        const v = parsed[key];
        if (!out[key] && typeof v === 'string' && v) out[key] = v;
      }
    }
  } catch {
    // A corrupt blob must never cost us the lead.
  }

  return out;
}

function mergeUtm(...sources: UtmParams[]): UtmParams {
  const out: UtmParams = {};
  for (const key of UTM_KEYS) {
    for (const src of sources) {
      const v = src[key];
      if (v) {
        out[key] = v;
        break;
      }
    }
  }
  return out;
}

function isWithinWindow(timestamp: unknown, days: number, now: Date): boolean {
  if (typeof timestamp !== 'string') return false;
  const t = Date.parse(timestamp);
  if (Number.isNaN(t)) return false;
  return now.getTime() - t <= days * 24 * 60 * 60 * 1000;
}

function readPosthogTouch(
  posthog: PostHogReader | null,
  scope: 'last' | 'first',
  now: Date,
): { ids: TrackingIds; raw: Record<string, string | null> } {
  const ids: TrackingIds = {};
  const raw: Record<string, string | null> = {};
  if (!posthog) return { ids, raw };

  for (const id of TRACKING_IDS) {
    const valueKey = scope === 'last' ? id : `${id}_first_touch`;
    const tsKey = scope === 'last' ? `${id}_at` : `${id}_first_touch_at`;
    const value = posthog.get_property(valueKey);
    const timestamp = posthog.get_property(tsKey);
    raw[valueKey] = typeof value === 'string' ? value : null;
    raw[tsKey] = typeof timestamp === 'string' ? timestamp : null;
    if (typeof value === 'string' && value && isWithinWindow(timestamp, WINDOW_DAYS[id], now)) {
      ids[id] = value;
    }
  }
  return { ids, raw };
}

function mergeTouch(...sources: TrackingIds[]): TrackingIds {
  const out: TrackingIds = {};
  for (const id of TRACKING_IDS) {
    for (const src of sources) {
      const v = src[id];
      if (v) {
        out[id] = v;
        break;
      }
    }
  }
  return out;
}

export interface GetAdAttributionOptions {
  urlParams: URLSearchParams;
  posthog: PostHogReader | null;
  cookies: string;
  sessionStorage?: SessionStorageReader | null;
  now?: Date;
}

export function getAdAttribution(opts: GetAdAttributionOptions): AdAttribution {
  const now = opts.now ?? new Date();
  const url = readUrl(opts.urlParams, opts.cookies);
  const session = readSession(opts.sessionStorage ?? null);
  const phLast = readPosthogTouch(opts.posthog, 'last', now);
  const phFirst = readPosthogTouch(opts.posthog, 'first', now);

  const lastTouch = mergeTouch(url, session, phLast.ids);
  const firstTouch = mergeTouch(phFirst.ids, lastTouch);

  const utmUrl = readUtmFromUrl(opts.urlParams);
  const utmSession = readUtmFromSession(opts.sessionStorage ?? null);
  const utm = mergeUtm(utmUrl, utmSession);

  return {
    lastTouch,
    firstTouch,
    utm,
    trail: {
      captured_at: now.toISOString(),
      url,
      session,
      posthog: { last_touch: phLast.raw, first_touch: phFirst.raw },
      resolved: { last_touch: lastTouch, first_touch: firstTouch },
      utm: { url: utmUrl, session: utmSession, resolved: utm },
    },
  };
}

export function classifyAcquisition(ids: TrackingIds): 'paid_google' | 'paid_meta' | 'organic' {
  if (GOOGLE_FAMILY.some((k) => ids[k])) return 'paid_google';
  if (META_CLICK.some((k) => ids[k])) return 'paid_meta';
  return 'organic';
}
